/**
 * Small pieces Settings → Integrations, Browse apps and the connect window
 * share (Paper 40–45): an app's tile, a state's dot and words, ★ Primary,
 * and the banner at the top of the page.
 */
import { cn } from "@crew/ui/lib/utils";
import { Check, CircleAlert, Plug, Star, X } from "lucide-react";
import { useState, type ReactNode } from "react";
import { errorHint } from "../../app/api";
import { appIconOf } from "./app-icons";
import type { Banner, Tone } from "./integrations-view";

type Drawn = NonNullable<ReturnType<typeof appIconOf>>;

/** What's in the tile: the drawn icon, the logo file, the plug or the two letters. */
function Glyph({
  app,
  drawn,
  logo,
  mark,
  muted,
  glyph,
  onLogoFailed,
}: {
  app: string;
  drawn: Drawn | null;
  logo: string | null;
  mark?: string;
  muted: boolean;
  glyph: string;
  onLogoFailed: () => void;
}) {
  if (drawn) {
    return (
      <svg
        viewBox="0 0 24 24"
        focusable="false"
        fill={muted ? "currentColor" : drawn.color}
        className={glyph}
      >
        <path d={drawn.path} />
      </svg>
    );
  }
  if (logo) {
    return (
      <img
        src={logo}
        alt=""
        loading="lazy"
        decoding="async"
        width={20}
        height={20}
        onError={onLogoFailed}
        className={cn("size-5 object-contain", muted && "opacity-50 grayscale")}
      />
    );
  }
  if (app.startsWith("mcp:")) return <Plug className={glyph} />;
  return <span className="text-xs font-semibold">{mark}</span>;
}

/** A tile's frame: white behind a drawn icon or a logo, else the secondary tint. */
function tileClass(kind: string | undefined, size: "md" | "lg", muted: boolean) {
  const white = kind !== "mcp" && kind !== "mark";
  return cn(
    "flex shrink-0 items-center justify-center rounded-md",
    white ? "bg-card shadow-[0_0_0_1px_rgb(31_42_68/0.1)]" : "bg-secondary",
    size === "lg" ? "size-9" : "size-8",
    muted ? "text-hint" : "text-foreground",
  );
}

/**
 * An app's tile: its drawn icon (`app-icons.ts`) on white; a directory
 * entry's logo, a checked-in file (`worker/integrations/mcp-logos.ts`),
 * on white; else its two-letter mark, or for an MCP server a plug. A logo that
 * fails to load falls back the same way. Decorative: the name is beside it.
 */
export function AppIcon({
  app,
  icon,
  logo,
  mark,
  size = "md",
  muted = false,
}: {
  app: string;
  icon?: string;
  logo?: string;
  mark?: string;
  size?: "md" | "lg";
  muted?: boolean;
}) {
  const [failed, setFailed] = useState<string | null>(null);
  const drawn = appIconOf(icon);
  const shown = logo && logo !== failed ? logo : null;
  const kind = drawn ? icon : shown ? "logo" : app.startsWith("mcp:") ? "mcp" : "mark";
  return (
    <span aria-hidden data-app-icon={kind} className={tileClass(kind, size, muted)}>
      <Glyph
        app={app}
        drawn={drawn}
        logo={shown}
        mark={mark}
        muted={muted}
        glyph={size === "lg" ? "size-4.5" : "size-4"}
        onLogoFailed={() => setFailed(logo ?? null)}
      />
    </span>
  );
}

const TEXT: Record<Tone, string> = {
  moss: "text-moss",
  copper: "text-copper",
  red: "text-destructive",
  grey: "text-muted-foreground",
};
const DOT: Record<Tone, string> = {
  moss: "bg-moss",
  copper: "bg-copper",
  red: "bg-destructive",
  grey: "bg-foreground/30",
};

export function StateLabel({
  tone,
  children,
  className,
}: {
  tone: Tone;
  children: string;
  className?: string;
}) {
  return (
    <span className={cn("flex items-center gap-1.5 text-meta font-medium", TEXT[tone], className)}>
      <span aria-hidden className={cn("size-1.5 shrink-0 rounded-full", DOT[tone])} />
      {children}
    </span>
  );
}

export function PrimaryTag() {
  return (
    <span className="flex shrink-0 items-center gap-1 rounded-tag bg-secondary px-1.5 py-px text-xs font-medium text-foreground">
      <Star className="size-2.5 fill-current" aria-hidden />
      Primary
    </span>
  );
}

/** The banner a connect comes back to, or a change's notice (Paper 44). */
export function ReturnBanner({ banner, onClose }: { banner: Banner; onClose: () => void }) {
  const Icon = banner.tone === "moss" ? Check : CircleAlert;
  return (
    <div
      role="status"
      data-banner={banner.tone}
      className={cn(
        "flex items-center gap-3 rounded-lg px-4 py-3",
        banner.tone === "moss" && "bg-moss/[0.06] shadow-[0_0_0_1px_rgb(63_107_78/0.25)]",
        banner.tone === "red" && "bg-destructive/[0.04] shadow-[0_0_0_1px_rgb(162_59_47/0.25)]",
        banner.tone === "neutral" && "bg-card shadow-[0_0_0_1px_rgb(31_42_68/0.08)]",
      )}
    >
      <Icon
        aria-hidden
        className={cn(
          "size-4 shrink-0",
          banner.tone === "moss"
            ? "text-moss"
            : banner.tone === "red"
              ? "text-destructive"
              : "text-muted-foreground",
        )}
      />
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className={cn("text-sm text-foreground", banner.detail && "font-medium")}>
          {banner.title}
        </span>
        {banner.detail ? (
          <span className="text-meta text-muted-foreground">{banner.detail}</span>
        ) : null}
      </div>
      <button
        type="button"
        aria-label="Dismiss"
        onClick={onClose}
        className="flex size-6 shrink-0 items-center justify-center rounded text-muted-foreground hover:text-foreground"
      >
        <X className="size-3" />
      </button>
    </div>
  );
}

export const UNAVAILABLE = "Apps aren't available right now. Your crew answers without them.";

/** A red action: Reconnect, Try again, Disconnect, Remove. */
export const SOLID_RED = "bg-destructive text-white hover:bg-destructive/90";

/**
 * A section's head: its title, one line, and its action (Paper 40). `inline`:
 * on a desktop the line follows the title on its baseline (Paper 74).
 */
export function SectionHead({
  title,
  line,
  action,
  inline = false,
}: {
  title: string;
  line: ReactNode;
  action?: ReactNode;
  inline?: boolean;
}) {
  return (
    <div
      className={cn(
        "flex justify-between gap-5",
        inline ? "items-end md:items-baseline" : "items-end",
      )}
    >
      <div
        className={cn(
          "flex flex-col",
          inline ? "gap-0.5 md:flex-row md:items-baseline md:gap-2.5" : "gap-1",
        )}
      >
        <h2 className="text-question leading-6 font-semibold tracking-tight text-foreground">
          {title}
        </h2>
        <p className="text-meta leading-[18px] text-muted-foreground">{line}</p>
      </div>
      {action}
    </div>
  );
}

/** A change's error, under the row it's about. */
export function ErrorLine({ error }: { error: unknown }) {
  return error ? <p className="text-meta text-destructive">{errorHint(error)}</p> : null;
}
